/**
 * Iris Operator — the Fin-style meta-agent. It never touches the live reply
 * path; instead it mines the tenant's own Iris performance (topics where Iris
 * got CONSTRAINED, escalated to a human, or scored a low CX) and PROPOSES the
 * concrete config change that would close the biggest gap:
 *   - GUIDANCE  — a new natural-language behavior rule
 *   - PROCEDURE — a new multi-step SOP for a recurring request
 *   - KNOWLEDGE — a missing fact/answer
 * Each proposal carries a ready-to-apply `payload` (the config object a human
 * copies into the live config) and moves through a verdict lifecycle
 * (Proposed → Accepted / Rejected). Accepting only records the decision.
 *
 * Reached through the operations proxy at `/api/messaging/iris/operator`
 * (operations is the FE's auth boundary + RBAC gate; it forwards to
 * ai-operations with the shared service key), mirroring the sibling
 * `recommendations.ts` / `review.ts` hooks.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/operator';

/** The kind of config change a proposal would apply. */
export type OperatorProposalType = 'GUIDANCE' | 'PROCEDURE' | 'KNOWLEDGE';

/** Verdict lifecycle of a proposal. */
export type OperatorProposalStatus =
  /** Freshly minted by an analysis pass, awaiting a human verdict. */
  | 'PROPOSED'
  /** A human agreed — the decision is recorded (config not auto-applied). */
  | 'ACCEPTED'
  /** A human dismissed it. */
  | 'REJECTED';

/** The two terminal verdicts a human can record on a proposal. */
export type OperatorProposalVerdict = Extract<OperatorProposalStatus, 'ACCEPTED' | 'REJECTED'>;

/** The ready-to-apply config the human copies in, for a GUIDANCE proposal. */
export interface GuidancePayload {
  text: string;
  category: string;
  channels: string[];
  enabled: boolean;
}

/** The ready-to-apply config for a PROCEDURE proposal. */
export interface ProcedurePayload {
  name: string;
  triggerIntent: string;
  instructions: string;
  requiresVerification: boolean;
  endState: string;
  enabled: boolean;
}

/** The ready-to-apply config for a KNOWLEDGE proposal. */
export interface KnowledgePayload {
  title: string;
  content: string;
  category: string;
  enabled: boolean;
}

/** The `payload` shape depends on the proposal `type`. */
export type OperatorProposalPayload =
  | GuidancePayload
  | ProcedurePayload
  | KnowledgePayload
  | Record<string, unknown>;

/** One config-change proposal minted by the Operator. */
export interface OperatorProposal {
  id: string;
  type: OperatorProposalType;
  status: OperatorProposalStatus;
  title: string;
  description: string;
  /** One sentence citing the struggling-topic evidence. */
  rationale: string;
  /** The ready-to-apply config a human copies into the live config. */
  payload: OperatorProposalPayload;
  /** Estimated struggling conversations this would help — drives ranking. */
  impact: number;
  createdAt: string;
  updatedAt: string;
}

export interface OperatorProposalList {
  items: OperatorProposal[];
}

const operatorRoot = ['messaging', 'iris-operator'] as const;
export const irisOperatorKey = (status?: OperatorProposalStatus) =>
  [...operatorRoot, 'list', status ?? 'ALL'] as const;

/** The Operator proposal board, most impactful first, optionally filtered by
 * verdict status. `status` omitted = all. */
export function useOperatorProposals(status?: OperatorProposalStatus) {
  return useQuery({
    queryKey: irisOperatorKey(status),
    queryFn: () => {
      const qs = status ? `?status=${status}` : '';
      return apiGet<OperatorProposalList>(operationsClient, `${BASE}${qs}`);
    },
    staleTime: 15_000,
  });
}

/** Run a fresh meta-analysis pass over recent Iris performance and mint
 * proposals for the biggest gaps. Optionally override the lookback window
 * (days). */
export function useRunOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days?: number) => {
      const qs = days && days > 0 ? `?days=${Math.floor(days)}` : '';
      return apiPost<{ created: number }, undefined>(operationsClient, `${BASE}/analyze${qs}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: operatorRoot });
    },
  });
}

/** Record a human verdict (Accept / Reject) on a single proposal. */
export function useSetProposalStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: OperatorProposalVerdict }) =>
      apiPut<OperatorProposal, { status: OperatorProposalVerdict }>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}/status`,
        { status },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: operatorRoot });
    },
  });
}
